import type { TFunction } from 'i18next';

import type { FeedingDashboardConfig } from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import {
    confIntervalTone,
    fallthroughTone,
    feedVsPlanTone,
    type KpiTone,
    sfrTone,
} from '../feedingChartColors';
import { formatPercent } from '../percentFormat';
import { formatSettingPercent } from '../settingPercent';

/** A cell a settings band can mark, with what its band is judged on. */
export type InfoBand =
    | { kind: 'sfr'; pct: number | null; config: FeedingDashboardConfig }
    | { kind: 'interval'; ratio: number | null; config: FeedingDashboardConfig }
    | { kind: 'feedVsPlan'; pct: number | null }
    | { kind: 'pellet'; pct: number | null; config: FeedingDashboardConfig };

/** The band's tone, by the same rule the cell's mark is drawn with. */
export function infoBandTone(band: InfoBand): KpiTone {
    switch (band.kind) {
        case 'sfr':
            return sfrTone(
                band.pct,
                band.config.sfrHealthyMinPct,
                band.config.sfrHealthyMaxPct,
                band.config.sfrOverfeedPct
            );
        case 'interval':
            return confIntervalTone(
                band.ratio,
                band.config.confIntervalGoodPct,
                band.config.confIntervalWarnPct
            );
        case 'feedVsPlan':
            return feedVsPlanTone(band.pct);
        case 'pellet':
            return fallthroughTone(
                band.pct,
                band.config.fallthroughWarnPct,
                band.config.fallthroughAlertPct
            );
    }
}

/**
 * The title of a marked cell: which band from the settings the value
 * falls in, from the pen's own config. None for a value in band, which is unmarked.
 */
export function infoCellTitle(band: InfoBand, t: TFunction, locale: Locale): string | undefined {
    const tone = infoBandTone(band);
    if (tone !== 'warning' && tone !== 'alert') return undefined;
    const setting = (value: number) => formatSettingPercent(value, locale);
    switch (band.kind) {
        case 'sfr':
            return tone === 'alert'
                ? t('feedingModule.table.info.sfrOverfeed', {
                      value: setting(band.config.sfrOverfeedPct),
                  })
                : t('feedingModule.table.info.sfrOutside', {
                      min: setting(band.config.sfrHealthyMinPct),
                      max: setting(band.config.sfrHealthyMaxPct),
                  });
        case 'interval':
            return intervalTitle(band.ratio, tone, band.config, t, locale);
        case 'feedVsPlan':
            return t('feedingModule.table.info.belowPlan');
        case 'pellet':
            return tone === 'alert'
                ? t('feedingModule.kpi.fallthroughHigh', {
                      value: setting(band.config.fallthroughAlertPct),
                  })
                : t('feedingModule.kpi.fallthroughElevated', {
                      value: setting(band.config.fallthroughWarnPct),
                  });
    }
}

function intervalTitle(
    ratio: number | null,
    tone: 'warning' | 'alert',
    config: FeedingDashboardConfig,
    t: TFunction,
    locale: Locale
): string | undefined {
    if (ratio == null) return undefined;
    const limit = tone === 'alert' ? config.confIntervalWarnPct : config.confIntervalGoodPct;
    const pct = ratio * 100;
    return t('feedingModule.table.info.intervalWide', {
        ratio: formatPercent(pct, ratioDigits(pct, limit), { locale }),
        limit: formatSettingPercent(limit, locale),
    });
}

/**
 * One decimal, or more where one would round the ratio onto the setting it passed: "3.0%,
 * wider than the 3% setting" would read as a contradiction.
 */
function ratioDigits(pct: number, limit: number): number {
    for (let digits = 1; digits < 3; digits += 1) {
        if (Number(pct.toFixed(digits)) > limit) return digits;
    }
    return 3;
}

/** Several titles for one cell, one per line, or none when none applies. */
export function joinTitles(...titles: (string | undefined)[]): string | undefined {
    const present = titles.filter((title): title is string => Boolean(title));
    return present.length > 0 ? present.join('\n') : undefined;
}
